<template>
    <div>
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">Base Fees</div>
        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
            <div v-if="shownFees.length > 0" class="divide-y divide-gray-200">
                <kiosk-base-fee-row
                    v-for="(fee, index) in shownFees"
                    :key="fee.key"
                    v-model:values="values"
                    :fee="fee"
                    :item="props.item"
                    :editing="props.editing"
                    :errors="props.errors"
                    :routingOptions="props.routingOptions"
                    :first="0 === index"
                    :last="shownFees.length - 1 === index"
                    @remove="remove(fee)"
                />
            </div>
            <div v-else class="px-6 py-4 text-sm text-gray-500" data-test="no-base-fees">No base fees</div>
        </div>
        <div v-if="props.editing" class="mt-2 px-2 flex">
            <vx-dropdown teleport>
                <vx-button type="button" size="sm" color="secondary" data-test="add-base-fee">
                    Add fee
                    <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
                <template v-slot:items>
                    <vx-dropdown-item
                        v-for="fee in props.fees"
                        :key="fee.key"
                        :disabled="listed.includes(fee.key)"
                        :data-test="`add-base-fee-${testId(fee.key)}`"
                        @click="add(fee)"
                    >
                        {{ fee.label }}
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { VxButton, VxDropdown, VxDropdownItem } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import KioskBaseFeeRow from '~/components/kiosk/kiosk-billing/KioskBaseFeeRow.vue';
import {
    type BaseFee,
    type BaseFeeRoutingOption,
    type PricingValues,
    isBaseFeeInUse,
    testId,
} from '~/components/kiosk/kiosk-billing/baseFees';

const props = defineProps<{
    fees: readonly BaseFee[];
    item: PricingValues;
    editing: boolean;
    errors: Record<string, string>;
    routingOptions?: readonly BaseFeeRoutingOption[];
}>();

const values = defineModel<Record<string, unknown>>('values', { required: true });
const listed = defineModel<string[]>('listed', { required: true });

const shownFees = computed(() =>
    props.editing
        ? props.fees.filter((fee) => listed.value.includes(fee.key))
        : props.fees.filter((fee) => isBaseFeeInUse(fee, props.item))
);

const add = (fee: BaseFee): void => {
    listed.value = [...listed.value, fee.key];
    values.value = {
        ...values.value,
        ...('flat' === fee.kind
            ? { [fee.amountField]: '0.00000' }
            : { [fee.quantityField]: '1', [fee.rateField]: '0.00000' }),
    };
};

const remove = (fee: BaseFee): void => {
    listed.value = listed.value.filter((key) => key !== fee.key);
    values.value = {
        ...values.value,
        ...('flat' === fee.kind
            ? { [fee.amountField]: '0.00000' }
            : { [fee.quantityField]: '0', [fee.rateField]: '0.00000' }),
    };
};
</script>
